import { InView } from 'react-intersection-observer';
import { IconArrowRightRegular, IconInfoRegular, Text } from '@croquiscom-pvt/zds';
import { vars } from '@croquiscom-pvt/zds/dist/tokens';
import { css } from '@emotion/react';
import styled from '@emotion/styled';

import { SellerBadge, ShopUxUbl, TextOverFlowCss } from '@domains/storehome-common';

interface Props {
  name: string;
  seller_badges: SellerBadge[];
  additional_badge?: string;
  has_info_url?: boolean;
  onShopNameClick?: () => void;
  onInfoClick?: () => void;
  onBadgeImpression?: (ubl?: ShopUxUbl | null) => void;
}

export const ShopBasicInfo = ({
  name,
  seller_badges,
  additional_badge,
  has_info_url = false,
  onShopNameClick,
  onInfoClick,
  onBadgeImpression,
}: Props) => {
  const hide_attributes = seller_badges.length === 0 && !additional_badge;

  return (
    <Information>
      <ShopNameWrapper onClick={onShopNameClick}>
        <Text
          as='h2'
          variant='Head_20_Multiline_Bold'
          color='textStrong'
          css={css`
            ${TextOverFlowCss}
          `}
        >
          {name}
        </Text>
        {has_info_url && (
          <LandingIcon>
            <IconArrowRightRegular size={20} color='iconTertiary' />
          </LandingIcon>
        )}
      </ShopNameWrapper>
      {!hide_attributes && (
        <AttributesWrapper>
          <Badges>
            {seller_badges.map((badge) => (
              <InView
                key={badge.text.text}
                threshold={0.1}
                triggerOnce={true}
                onChange={(inView) => {
                  if (inView && onBadgeImpression) {
                    onBadgeImpression(badge.ubl);
                  }
                }}
              >
                {({ ref }) => (
                  <Badge
                    ref={ref}
                    className='grade-badge'
                    variant='Caption_10_Multiline_Regular'
                    color='textSecondary'
                    as='span'
                    css={css`
                      padding: 2.5px 4px 3.5px 3px;
                      background-color: ${badge.background_color?.normal ?? vars.color.fillTertiary};
                    `}
                  >
                    {badge.icon_url && <BadgeImage src={badge.icon_url.normal ?? ''} alt='셀러 등급 뱃지 이미지' />}
                    <BadgeText>{badge.text.text}</BadgeText>
                  </Badge>
                )}
              </InView>
            ))}
            {additional_badge && (
              <Badge
                className='additional-badge'
                variant='Caption_10_Multiline_Regular'
                color='textSecondary'
                as='span'
                css={css`
                  background-color: ${vars.color.fillTertiary};
                `}
              >
                <BadgeText>{additional_badge}</BadgeText>
              </Badge>
            )}
          </Badges>
          {has_info_url && (
            <InfoIcon onClick={onInfoClick}>
              <IconInfoRegular size={14} color='iconTertiary' />
            </InfoIcon>
          )}
        </AttributesWrapper>
      )}
    </Information>
  );
};

const Information = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
`;

const ShopNameWrapper = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 4px;
  &:hover {
    cursor: pointer;
  }
`;

const LandingIcon = styled.button`
  height: 20px;
  margin: 0;
  padding: 0;
  border: none;
  background-color: transparent;
  flex: 0 0 20px;
`;

const AttributesWrapper = styled.div`
  height: 20px;
  display: flex;
  align-items: center;
  gap: 4px;
`;

const Badges = styled.div`
  display: flex;
  gap: 4px;
  align-items: center;
  min-width: 0;
  overflow: hidden;
`;

const Badge = styled(Text)`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2.5px 4px 3.5px;
  border-radius: 2px;
  max-width: 100%;
  min-width: 0;
  overflow: hidden;

  /* 우선순위에 따른 flex-shrink 설정 */
  &:first-of-type {
    flex-shrink: 0; /* 가장 마지막에 줄어듦 */
  }

  &:nth-of-type(2) {
    flex-shrink: 1; /* 두 번째로 줄어듦 */
  }

  &.additional-badge {
    flex-shrink: 2; /* 가장 먼저 줄어듦 */
  }
`;

const BadgeImage = styled.img`
  width: 12px;
  height: 12px;
  flex-shrink: 0;
`;

const BadgeText = styled.span`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
`;

const InfoIcon = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  margin: 0;
  padding: 0;
  border: none;
  background-color: transparent;
  position: relative;
  left: -13px;
  &:hover {
    cursor: pointer;
  }
`;
